import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';

import {
  type Entity,
  type IdentityEntityGetEntitiesData,
  type IdentityEntityGetEntitiesResponse,
  identityEntityGetEntities,
  identityEntityGetEntity,
} from '@column/api';
import { Dropdown, DropdownOption } from '@column/column-ui-kit';

import { useDebounce } from '~/hooks/useDebounce';
import {
  type HighlightSuffixCandidate,
  getHighlightedField,
  getHighlightedSuffix,
  renderHighlight,
} from '~/lib/search/highlight';
import { useSessionStore } from '~/stores/Session';

// --- Label components ---

type EntityFilters = NonNullable<IdentityEntityGetEntitiesData['query']>;

const entityDropdownListQueryKey = (platformId?: string, isSandbox?: boolean, query?: EntityFilters) =>
  ['entityDropdown', 'list', platformId, isSandbox, query] as const;

const SUFFIX_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'PERSON_DETAILS.EMAIL', label: 'Email' },
  { field: 'PERSON_DETAILS.PHONE_NUMBER', label: 'Phone Number' },
  { field: 'PERSON_DETAILS.SSN', label: 'SSN' },
  { field: 'BUSINESS_DETAILS.EIN', label: 'EIN' },
  { field: 'BUSINESS_DETAILS.WEBSITE', label: 'Website' },
  { field: 'DETAILS_ID', label: 'Details ID' },
  { field: 'ID', label: 'ID' },
];

const HighlightedLabel: FC<{ entity: Entity }> = ({ entity }) => {
  const highlightedName = getHighlightedField(entity.search_metadata, 'NAME');
  return <>{highlightedName ? renderHighlight(highlightedName) : entity.name || 'Unnamed'}</>;
};

const PlainLabel: FC<{ entity: Entity }> = ({ entity }) => <>{entity.name || 'Unnamed'}</>;

const EntityOptionLabel: FC<{
  entity: Entity;
  highlightSearch: boolean;
  hasSearchQuery: boolean;
}> = ({ entity, highlightSearch, hasSearchQuery }) => {
  if (hasSearchQuery && highlightSearch && entity.search_metadata?.highlights) {
    return <HighlightedLabel entity={entity} />;
  }
  return <PlainLabel entity={entity} />;
};

const EntityOptionSmall: FC<{ entity: Entity }> = ({ entity }) => {
  const match = getHighlightedSuffix(entity.search_metadata, SUFFIX_CANDIDATES);
  if (!match) return null;
  return (
    <>
      {match.label}: {renderHighlight(match.fragment)}
    </>
  );
};

// --- Shared hook ---

interface UseEntityOptionsConfig {
  value?: string | null;
}

export const useEntityOptions = (config: UseEntityOptionsConfig = {}) => {
  const { value } = config;
  const platformId = useSessionStore((state) => state.currentPlatform?.id);
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const [searchText, setSearchText] = useState<string | undefined>();

  const effectiveQuery = useMemo(() => {
    if (searchText) return { query: searchText };
    return undefined;
  }, [searchText]);

  const entitiesQuery = useInfiniteQuery({
    queryKey: entityDropdownListQueryKey(platformId, isSandbox, effectiveQuery),
    queryFn: async ({ pageParam, signal }) => {
      const startingAfter = typeof pageParam === 'string' ? pageParam : undefined;
      const { data } = await identityEntityGetEntities({
        query: { ...effectiveQuery, starting_after: startingAfter },
        signal,
        throwOnError: true,
      });
      return data as IdentityEntityGetEntitiesResponse;
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more) return undefined;
      return lastPage.entities?.[lastPage.entities.length - 1]?.id;
    },
    initialPageParam: undefined as string | undefined,
    placeholderData: keepPreviousData,
  });

  const resolvedId = value ?? undefined;

  const entities = useMemo(() => {
    const entries = entitiesQuery.data?.pages.flatMap((page) => page?.entities ?? []) ?? [];
    return [...new Map(entries.map((entry) => [entry.id, entry])).values()];
  }, [entitiesQuery.data]);

  const activeEntityQuery = useQuery({
    queryKey: ['entityDropdown', 'get', platformId, isSandbox, resolvedId],
    queryFn: async () => {
      const { data } = await identityEntityGetEntity({
        path: { entity_id: resolvedId! },
        throwOnError: true,
      });
      return data;
    },
    enabled: Boolean(resolvedId),
    placeholderData: () => entities.find((e) => e.id === resolvedId),
  });

  const activeEntity = activeEntityQuery.data as Entity | undefined;
  const hasSearchQuery = Boolean(searchText);

  const [search] = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (entity: Entity, highlightSearch: boolean): DropdownOption => ({
      label: <EntityOptionLabel entity={entity} highlightSearch={highlightSearch} hasSearchQuery={hasSearchQuery} />,
      small:
        hasSearchQuery && highlightSearch && entity.search_metadata?.highlights ? (
          <EntityOptionSmall entity={entity} />
        ) : undefined,
      value: entity.id,
    }),
    [hasSearchQuery]
  );

  const options: DropdownOption[] = useMemo(() => entities.map((e) => toOption(e, true)), [entities, toOption]);

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      entities.find((e) => e.id === resolvedId) || (activeEntity?.id === resolvedId ? activeEntity : undefined);

    return selected ? [toOption(selected, false)] : [];
  }, [activeEntity, resolvedId, entities, toOption]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData } = entitiesQuery;
  const handleScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage && !isPlaceholderData) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isPlaceholderData]);

  return {
    options,
    hiddenOptions,
    search,
    handleScrollEnd,
    hasSearchQuery,
    entities,
    activeEntity,
  };
};

// --- Component ---

export interface EntityDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  searchLabel?: string;
}

export const EntityDropdown: FC<EntityDropdownProps> = ({
  value,
  onChange,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
  isLoading,
  searchLabel,
}) => {
  const { options, hiddenOptions, search, handleScrollEnd } = useEntityOptions({ value });

  return (
    <Dropdown
      options={options}
      hiddenOptions={hiddenOptions}
      active={value ?? undefined}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      emptyMessage="No entities found"
      onSearchChange={(query: string) => search(query)}
      onChange={onChange}
      onScrollEnd={handleScrollEnd}
      hasError={hasError}
      isDisabled={isDisabled}
      isLoading={isLoading}
      placeholder={placeholder}
      searchLabel={searchLabel}
      search
    />
  );
};
